import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ThemeSettings {
  theme: 'classic' | 'modern' | 'terminal' | 'sepia' | 'midnight' | 'eink';
  darkMode: boolean;
  highContrast: boolean;
  dyslexiaFont: boolean;
}

interface ThemeContextValue extends ThemeSettings {
  setTheme: (theme: 'classic' | 'modern' | 'terminal' | 'sepia' | 'midnight' | 'eink') => void;
  setDarkMode: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  setDyslexiaFont: (enabled: boolean) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'theme-settings';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setThemeState] = useState<'classic' | 'modern' | 'terminal' | 'sepia' | 'midnight' | 'eink'>('classic');
  const [darkMode, setDarkModeState] = useState(true);
  const [highContrast, setHighContrastState] = useState(false);
  const [dyslexiaFont, setDyslexiaFontState] = useState(false);

  // Apply theme classes to DOM
  const applyTheme = (settings: ThemeSettings) => {
    const html = document.documentElement;
    const body = document.body;

    // Remove all theme classes first
    html.classList.remove('theme-classic', 'theme-modern', 'theme-terminal', 'theme-sepia', 'theme-midnight', 'theme-eink', 'dark', 'light', 'high-contrast');
    body.classList.remove('dyslexia-font');

    // Apply theme class
    html.classList.add(`theme-${settings.theme}`);
    
    // Apply dark/light mode
    html.classList.add(settings.darkMode ? 'dark' : 'light');
    
    // Apply high contrast
    if (settings.highContrast) {
      html.classList.add('high-contrast');
    }
    
    // Apply dyslexia font
    if (settings.dyslexiaFont) {
      body.classList.add('dyslexia-font');
    }
  };

  // Save to localStorage and Supabase
  const saveSettings = async (settings: ThemeSettings) => {
    // Save to localStorage immediately
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    
    // Debounced save to Supabase
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('settings')
          .eq('id', user.id)
          .single();

        const currentSettings = (profile?.settings && typeof profile.settings === 'object') 
          ? profile.settings as Record<string, any>
          : {};
        
        await supabase
          .from('profiles')
          .update({
            settings: {
              ...currentSettings,
              ...settings,
            }
          })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Error saving theme to Supabase:', error);
    }
  };

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // First try localStorage for instant application
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const settings = JSON.parse(stored) as ThemeSettings;
          setThemeState(settings.theme);
          setDarkModeState(settings.darkMode);
          setHighContrastState(settings.highContrast);
          setDyslexiaFontState(settings.dyslexiaFont);
          applyTheme(settings);
        }

        // Then load from Supabase to sync
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('settings')
            .eq('id', user.id)
            .single();

          if (profile?.settings && typeof profile.settings === 'object') {
            const dbSettings = profile.settings as Record<string, any>;
            const settings: ThemeSettings = {
              theme: (dbSettings.theme as 'classic' | 'modern' | 'terminal' | 'sepia' | 'midnight' | 'eink') || 'classic',
              darkMode: dbSettings.darkMode !== undefined ? Boolean(dbSettings.darkMode) : true,
              highContrast: Boolean(dbSettings.highContrast),
              dyslexiaFont: Boolean(dbSettings.dyslexiaFont),
            };
            
            setThemeState(settings.theme);
            setDarkModeState(settings.darkMode);
            setHighContrastState(settings.highContrast);
            setDyslexiaFontState(settings.dyslexiaFont);
            applyTheme(settings);
            
            // Update localStorage with DB values
            localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
          }
        }
      } catch (error) {
        console.error('Error loading theme settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Update functions
  const setTheme = (newTheme: 'classic' | 'modern' | 'terminal' | 'sepia' | 'midnight' | 'eink') => {
    setThemeState(newTheme);
    const settings = { theme: newTheme, darkMode, highContrast, dyslexiaFont };
    applyTheme(settings);
    saveSettings(settings);
  };

  const setDarkMode = (enabled: boolean) => {
    setDarkModeState(enabled);
    const settings = { theme, darkMode: enabled, highContrast, dyslexiaFont };
    applyTheme(settings);
    saveSettings(settings);
  };

  const setHighContrast = (enabled: boolean) => {
    setHighContrastState(enabled);
    const settings = { theme, darkMode, highContrast: enabled, dyslexiaFont };
    applyTheme(settings);
    saveSettings(settings);
  };

  const setDyslexiaFont = (enabled: boolean) => {
    setDyslexiaFontState(enabled);
    const settings = { theme, darkMode, highContrast, dyslexiaFont: enabled };
    applyTheme(settings);
    saveSettings(settings);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        darkMode,
        highContrast,
        dyslexiaFont,
        setTheme,
        setDarkMode,
        setHighContrast,
        setDyslexiaFont,
        isLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
